import { Table, Tag, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import type { ColumnType } from "antd/es/table";
import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { actions as routerActions } from "redux-router5";
import type { Services, Ticket, UserLimitedList } from "../../generated-axios-client";
import { dateValue } from "../../../common/columns";
import { NoData } from "../../../common/components/NoDataComp";
import { TableActions } from "../../../common/components/TableActions";
import { routeNames } from "../../common/routes";
import { statusColorMap, statusLabelMap } from "../../ticket/ticketConstants";
import { AssigneeAvatarGroup } from "../../../common/components/AssigneeAvatarGroup";
import type { ClientViewRenderProps } from "./ClientViewRender";
import { Card } from "../../../common/components/Card";

const buildColumns = (
  intl: IntlShape,
  serviceObjects: Record<string, Services> | null
): ColumnType[] => [
  {
    key: "ticket_number",
    dataIndex: "ticket_number",
    title: intl.formatMessage({ id: "table_column_ticket_number" }),
    render: (value: number | null) =>
      value != null ? (
        <Typography.Text strong type="secondary">{`#${value}`}</Typography.Text>
      ) : (
        <NoData intl={intl} />
      ),
  },
  {
    key: "service",
    dataIndex: "service",
    title: intl.formatMessage({ id: "table_column_service" }),
    render: (value: string | null) => {
      const name = value ? serviceObjects?.[value]?.name : null;
      return name ?? <NoData intl={intl} />;
    },
  },
  {
    key: "assignees",
    dataIndex: "assignees",
    title: intl.formatMessage({ id: "table_column_assignee" }, { count: 1 }),
  },
  {
    key: "turbine",
    dataIndex: "turbine",
    title: intl.formatMessage({ id: "table_column_turbine" }),
    render: (value: string | null) => value ?? <NoData intl={intl} />,
  },
  {
    key: "created_by",
    dataIndex: "created_by",
    title: intl.formatMessage({ id: "table_column_created_by" }),
  },
  {
    key: "start",
    dataIndex: "start",
    title: intl.formatMessage({ id: "table_column_start" }),
    render: (value: string | null) =>
      value ? dateValue(value, intl) : <NoData intl={intl} />,
  },
  {
    key: "deadline",
    dataIndex: "deadline",
    title: intl.formatMessage({ id: "table_column_deadline" }),
    render: (value: string | null) =>
      value ? dateValue(value, intl) : <NoData intl={intl} />,
  },
  {
    key: "status",
    dataIndex: "status",
    title: intl.formatMessage({ id: "table_column_status" }),
    render: (value: string | null) =>
      value && value in statusLabelMap ? (
        <Tag
          color={value in statusColorMap ? statusColorMap[value] : "gray"}
          variant="outlined"
          styles={{ root: { fontWeight: 700 } }}
        >
          {statusLabelMap[value]}
        </Tag>
      ) : (
        <NoData intl={intl} />
      ),
  },
  {
    key: "actions",
    title: intl.formatMessage({ id: "table_column_actions" }),
    width: 80,
    render: () => null,
  },
];

type TicketsInfoProps = ClientViewRenderProps & { dispatch: Dispatch };

export const TicketsInfo = ({ intl, clientData, dispatch }: TicketsInfoProps) => {
  const { tickets, ticketSideload, serviceObjects } = clientData;

  const columns = buildColumns(intl, serviceObjects).map((col) => {
    if (col.key === "actions") {
      return {
        ...col,
        render: (_: unknown, record: unknown) => {
          const row = record as { id?: string };
          return (
            <TableActions
              actions={[
                {
                  key: "view",
                  icon: <EyeOutlined />,
                  tooltip: "View Work Order",
                  onClick: () =>
                    dispatch(routerActions.navigateTo(routeNames.TICKET_VIEW, { id: row.id })),
                },
              ]}
            />
          );
        },
      };
    }
    return col;
  });

  const rows = Object.values(tickets.results.tickets).map((ticket: Ticket) => {
    const assignees = ticket.assignees
      .map((ta) => (ta.assignee != null ? ticketSideload.assignees[ta.assignee] : null))
      .filter((u): u is UserLimitedList => u != null);

    const createdByUser =
      ticket.created_by != null ? ticketSideload.created_by[ticket.created_by] ?? null : null;

    return {
      key: ticket.id,
      id: ticket.id,
      ticket_number: ticket.ticket_number,
      turbine: ticketSideload.turbine[ticket.turbine]?.serial_number ?? null,
      service: ticket.service ?? null,
      status: ticket.status ?? null,
      assignees: <AssigneeAvatarGroup assignees={assignees} />,
      created_by: createdByUser ? (
        <AssigneeAvatarGroup assignees={[createdByUser]} />
      ) : (
        <NoData intl={intl} />
      ),
      start: ticket.start ?? null,
      deadline: ticket.deadline ?? null,
    };
  });

  return (
    <Card title={intl.formatMessage({ id: "work_orders" })}>
      <Table
        columns={columns}
        dataSource={rows}
        rowKey={(record) => (record as { id?: string }).id ?? ""}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: intl.formatMessage({ id: "common_no_data" }) }}
        size="small"
      />
    </Card>
  );
};
